// A curated, dependency-free emoji catalog for the insert-emoji picker.
//
// Why curated rather than a full library: the picker only needs the emoji that
// actually show up in marketing copy (faces, hands, hearts, celebration,
// commerce, food, nature, arrows). A hand-picked set keeps the bundle small, the
// grid scannable, and — critically — every glyph here renders in BOTH text
// pipelines: as literal Unicode in live HTML (mj-text / source_html) and via the
// Noto Color Emoji fallback baked into the CanvasKit raster path. We deliberately
// avoid skin-tone and ZWJ-sequence variants: they fragment the grid and some ZWJ
// sequences fall back to component glyphs in older clients.

export interface EmojiEntry {
  /** The emoji character to insert. */
  char: string;
  /** Human name, shown as the tooltip and matched by search. */
  name: string;
  /** Extra search terms beyond the name's own words. */
  keywords?: string[];
}

export interface EmojiCategory {
  id: string;
  label: string;
  emoji: EmojiEntry[];
}

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'smileys',
    label: 'Smileys',
    emoji: [
      { char: '\u{1F600}', name: 'grinning face', keywords: ['smile', 'happy'] },
      { char: '\u{1F603}', name: 'smiling face', keywords: ['happy', 'joy'] },
      { char: '\u{1F604}', name: 'smiling face with big eyes', keywords: ['happy'] },
      { char: '\u{1F601}', name: 'beaming face', keywords: ['grin'] },
      { char: '\u{1F606}', name: 'grinning squinting face', keywords: ['laugh'] },
      { char: '\u{1F605}', name: 'grinning face with sweat', keywords: ['relief'] },
      { char: '\u{1F923}', name: 'rolling on the floor laughing', keywords: ['rofl', 'lol'] },
      { char: '\u{1F602}', name: 'face with tears of joy', keywords: ['lol', 'laugh'] },
      { char: '\u{1F642}', name: 'slightly smiling face', keywords: ['smile'] },
      { char: '\u{1F609}', name: 'winking face', keywords: ['wink', 'flirt'] },
      { char: '\u{1F60A}', name: 'smiling face with smiling eyes', keywords: ['blush'] },
      { char: '\u{1F60D}', name: 'smiling face with heart-eyes', keywords: ['love', 'crush'] },
      { char: '\u{1F929}', name: 'star-struck', keywords: ['starstruck', 'wow', 'amazed'] },
      { char: '\u{1F618}', name: 'face blowing a kiss', keywords: ['kiss', 'love'] },
      { char: '\u{1F60B}', name: 'face savoring food', keywords: ['yum', 'delicious'] },
      { char: '\u{1F61C}', name: 'winking face with tongue', keywords: ['playful'] },
      { char: '\u{1F92A}', name: 'zany face', keywords: ['crazy', 'wild'] },
      { char: '\u{1F60E}', name: 'smiling face with sunglasses', keywords: ['cool', 'shades'] },
      { char: '\u{1F973}', name: 'partying face', keywords: ['party', 'celebrate', 'hooray'] },
      { char: '\u{1F970}', name: 'smiling face with hearts', keywords: ['love', 'adore'] },
      { char: '\u{1F914}', name: 'thinking face', keywords: ['hmm', 'consider'] },
      { char: '\u{1F644}', name: 'face with rolling eyes', keywords: ['eyeroll'] },
      { char: '\u{1F62E}', name: 'face with open mouth', keywords: ['wow', 'surprise'] },
      { char: '\u{1F631}', name: 'face screaming in fear', keywords: ['shock', 'omg'] },
      { char: '\u{1F525}', name: 'fire', keywords: ['hot', 'lit', 'flame', 'trending'] },
    ],
  },
  {
    id: 'gestures',
    label: 'Gestures',
    emoji: [
      { char: '\u{1F44B}', name: 'waving hand', keywords: ['hello', 'hi', 'wave', 'bye'] },
      { char: '\u{1F44D}', name: 'thumbs up', keywords: ['like', 'yes', 'approve'] },
      { char: '\u{1F44E}', name: 'thumbs down', keywords: ['dislike', 'no'] },
      { char: '\u{1F44F}', name: 'clapping hands', keywords: ['applause', 'bravo'] },
      { char: '\u{1F64C}', name: 'raising hands', keywords: ['celebrate', 'hooray', 'praise'] },
      { char: '\u{1F450}', name: 'open hands', keywords: ['welcome'] },
      { char: '\u{1F91D}', name: 'handshake', keywords: ['deal', 'partner', 'agreement'] },
      { char: '\u{1F64F}', name: 'folded hands', keywords: ['please', 'thanks', 'pray'] },
      { char: '\u{270C}\u{FE0F}', name: 'victory hand', keywords: ['peace'] },
      { char: '\u{1F44C}', name: 'OK hand', keywords: ['okay', 'perfect'] },
      { char: '\u{1F90F}', name: 'pinching hand', keywords: ['small', 'little'] },
      { char: '\u{1F918}', name: 'sign of the horns', keywords: ['rock'] },
      { char: '\u{1F919}', name: 'call me hand', keywords: ['shaka'] },
      { char: '\u{1F447}', name: 'backhand index pointing down', keywords: ['below', 'down'] },
      { char: '\u{1F448}', name: 'backhand index pointing left', keywords: ['left'] },
      { char: '\u{1F449}', name: 'backhand index pointing right', keywords: ['right', 'this'] },
      { char: '\u{1F446}', name: 'backhand index pointing up', keywords: ['above', 'up'] },
      { char: '\u{261D}\u{FE0F}', name: 'index pointing up', keywords: ['one', 'note'] },
      { char: '\u{1F4AA}', name: 'flexed biceps', keywords: ['strong', 'power', 'muscle'] },
      { char: '\u{1F9E0}', name: 'brain', keywords: ['smart', 'idea', 'think'] },
    ],
  },
  {
    id: 'hearts',
    label: 'Hearts',
    emoji: [
      { char: '\u{2764}\u{FE0F}', name: 'red heart', keywords: ['love'] },
      { char: '\u{1F9E1}', name: 'orange heart', keywords: ['love'] },
      { char: '\u{1F49B}', name: 'yellow heart', keywords: ['love'] },
      { char: '\u{1F49A}', name: 'green heart', keywords: ['love'] },
      { char: '\u{1F499}', name: 'blue heart', keywords: ['love'] },
      { char: '\u{1F49C}', name: 'purple heart', keywords: ['love'] },
      { char: '\u{1F5A4}', name: 'black heart', keywords: ['love'] },
      { char: '\u{1F90D}', name: 'white heart', keywords: ['love'] },
      { char: '\u{1F90E}', name: 'brown heart', keywords: ['love'] },
      { char: '\u{1F495}', name: 'two hearts', keywords: ['love'] },
      { char: '\u{1F496}', name: 'sparkling heart', keywords: ['love', 'shine'] },
      { char: '\u{1F49E}', name: 'revolving hearts', keywords: ['love'] },
      { char: '\u{1F493}', name: 'beating heart', keywords: ['love'] },
      { char: '\u{1F494}', name: 'broken heart', keywords: ['sad', 'heartbreak'] },
      { char: '\u{2B50}', name: 'star', keywords: ['favorite', 'rating', 'quality'] },
      { char: '\u{1F31F}', name: 'glowing star', keywords: ['sparkle', 'shine'] },
      { char: '\u{2728}', name: 'sparkles', keywords: ['new', 'shine', 'magic', 'clean'] },
      { char: '\u{1F4AB}', name: 'dizzy', keywords: ['star', 'sparkle'] },
      { char: '\u{1F49F}', name: 'heart decoration', keywords: ['love'] },
      { char: '\u{1F48C}', name: 'love letter', keywords: ['email', 'message'] },
    ],
  },
  {
    id: 'celebration',
    label: 'Celebration',
    emoji: [
      { char: '\u{1F389}', name: 'party popper', keywords: ['celebrate', 'tada', 'launch', 'congrats'] },
      { char: '\u{1F38A}', name: 'confetti ball', keywords: ['celebrate', 'party'] },
      { char: '\u{1F387}', name: 'sparkler', keywords: ['celebrate'] },
      { char: '\u{1F388}', name: 'balloon', keywords: ['party', 'birthday'] },
      { char: '\u{1F381}', name: 'wrapped gift', keywords: ['present', 'reward', 'gift'] },
      { char: '\u{1F382}', name: 'birthday cake', keywords: ['party', 'anniversary'] },
      { char: '\u{1F386}', name: 'fireworks', keywords: ['celebrate', 'new year'] },
      { char: '\u{1F3C6}', name: 'trophy', keywords: ['win', 'award', 'best'] },
      { char: '\u{1F947}', name: 'first place medal', keywords: ['win', 'gold', 'number one'] },
      { char: '\u{1F396}\u{FE0F}', name: 'military medal', keywords: ['award', 'honor'] },
      { char: '\u{1F451}', name: 'crown', keywords: ['king', 'queen', 'premium', 'vip'] },
      { char: '\u{1F48E}', name: 'gem stone', keywords: ['diamond', 'premium', 'value'] },
      { char: '\u{1F947}', name: 'gold medal', keywords: ['award'] },
      { char: '\u{1F37E}', name: 'bottle with popping cork', keywords: ['champagne', 'celebrate', 'cheers'] },
      { char: '\u{1F942}', name: 'clinking glasses', keywords: ['cheers', 'toast', 'celebrate'] },
    ],
  },
  {
    id: 'commerce',
    label: 'Shopping',
    emoji: [
      { char: '\u{1F6D2}', name: 'shopping cart', keywords: ['buy', 'checkout', 'store'] },
      { char: '\u{1F6CD}\u{FE0F}', name: 'shopping bags', keywords: ['buy', 'sale', 'store'] },
      { char: '\u{1F4B0}', name: 'money bag', keywords: ['cash', 'save', 'earn'] },
      { char: '\u{1F4B3}', name: 'credit card', keywords: ['pay', 'checkout'] },
      { char: '\u{1F3F7}\u{FE0F}', name: 'label', keywords: ['tag', 'price', 'sale'] },
      { char: '\u{1F4B8}', name: 'money with wings', keywords: ['spend', 'save'] },
      { char: '\u{1F4B5}', name: 'dollar banknote', keywords: ['money', 'cash', 'price'] },
      { char: '\u{1F4E6}', name: 'package', keywords: ['shipping', 'delivery', 'order'] },
      { char: '\u{1F69A}', name: 'delivery truck', keywords: ['shipping', 'free shipping'] },
      { char: '\u{2709}\u{FE0F}', name: 'envelope', keywords: ['email', 'mail', 'message'] },
      { char: '\u{1F4E7}', name: 'e-mail', keywords: ['email', 'inbox'] },
      { char: '\u{1F4EC}', name: 'open mailbox with raised flag', keywords: ['mail', 'new'] },
      { char: '\u{1F4F1}', name: 'mobile phone', keywords: ['app', 'call', 'text'] },
      { char: '\u{1F4BB}', name: 'laptop', keywords: ['computer', 'online', 'shop'] },
      { char: '\u{1F6CE}\u{FE0F}', name: 'bellhop bell', keywords: ['service', 'order'] },
      { char: '\u{1F3EA}', name: 'convenience store', keywords: ['shop', 'store'] },
      { char: '\u{1F4E3}', name: 'megaphone', keywords: ['announce', 'promo', 'shout'] },
      { char: '\u{1F4E2}', name: 'loudspeaker', keywords: ['announce', 'news'] },
      { char: '\u{1F514}', name: 'bell', keywords: ['notify', 'reminder', 'alert'] },
      { char: '\u{23F0}', name: 'alarm clock', keywords: ['time', 'reminder', 'hurry'] },
    ],
  },
  {
    id: 'food',
    label: 'Food & Drink',
    emoji: [
      { char: '\u{2615}', name: 'hot beverage', keywords: ['coffee', 'tea'] },
      { char: '\u{1F375}', name: 'teacup without handle', keywords: ['tea'] },
      { char: '\u{1F37A}', name: 'beer mug', keywords: ['drink', 'cheers'] },
      { char: '\u{1F377}', name: 'wine glass', keywords: ['drink'] },
      { char: '\u{1F378}', name: 'cocktail glass', keywords: ['drink'] },
      { char: '\u{1F964}', name: 'cup with straw', keywords: ['drink', 'soda'] },
      { char: '\u{1F355}', name: 'pizza', keywords: ['food'] },
      { char: '\u{1F354}', name: 'hamburger', keywords: ['food', 'burger'] },
      { char: '\u{1F32E}', name: 'taco', keywords: ['food'] },
      { char: '\u{1F369}', name: 'doughnut', keywords: ['food', 'donut', 'sweet'] },
      { char: '\u{1F36A}', name: 'cookie', keywords: ['food', 'sweet'] },
      { char: '\u{1F370}', name: 'shortcake', keywords: ['cake', 'sweet', 'dessert'] },
      { char: '\u{1F366}', name: 'soft ice cream', keywords: ['dessert', 'sweet'] },
      { char: '\u{1F34E}', name: 'red apple', keywords: ['fruit', 'healthy'] },
      { char: '\u{1F353}', name: 'strawberry', keywords: ['fruit'] },
      { char: '\u{1F345}', name: 'tomato', keywords: ['fruit', 'veggie'] },
      { char: '\u{1F951}', name: 'avocado', keywords: ['healthy', 'fruit'] },
      { char: '\u{1F955}', name: 'carrot', keywords: ['veggie', 'healthy'] },
      { char: '\u{1F957}', name: 'green salad', keywords: ['healthy', 'food'] },
      { char: '\u{1F36B}', name: 'chocolate bar', keywords: ['sweet', 'candy'] },
    ],
  },
  {
    id: 'nature',
    label: 'Nature',
    emoji: [
      { char: '\u{2600}\u{FE0F}', name: 'sun', keywords: ['summer', 'sunny', 'weather'] },
      { char: '\u{1F31E}', name: 'sun with face', keywords: ['summer', 'bright'] },
      { char: '\u{26C5}', name: 'sun behind cloud', keywords: ['weather'] },
      { char: '\u{2601}\u{FE0F}', name: 'cloud', keywords: ['weather'] },
      { char: '\u{1F327}\u{FE0F}', name: 'cloud with rain', keywords: ['weather', 'rain'] },
      { char: '\u{26A1}', name: 'high voltage', keywords: ['power', 'energy', 'fast', 'flash'] },
      { char: '\u{2744}\u{FE0F}', name: 'snowflake', keywords: ['winter', 'cold', 'sale'] },
      { char: '\u{1F308}', name: 'rainbow', keywords: ['pride', 'color'] },
      { char: '\u{1F31A}', name: 'new moon face', keywords: ['night'] },
      { char: '\u{1F319}', name: 'crescent moon', keywords: ['night', 'sleep'] },
      { char: '\u{1F33F}', name: 'herb', keywords: ['plant', 'green', 'natural', 'eco'] },
      { char: '\u{1F343}', name: 'leaf fluttering in wind', keywords: ['plant', 'eco', 'green'] },
      { char: '\u{1F331}', name: 'seedling', keywords: ['grow', 'new', 'eco'] },
      { char: '\u{1F332}', name: 'evergreen tree', keywords: ['tree', 'nature'] },
      { char: '\u{1F337}', name: 'tulip', keywords: ['flower', 'spring'] },
      { char: '\u{1F338}', name: 'cherry blossom', keywords: ['flower', 'spring'] },
      { char: '\u{1F339}', name: 'rose', keywords: ['flower', 'love'] },
      { char: '\u{1F33B}', name: 'sunflower', keywords: ['flower', 'summer'] },
      { char: '\u{1F30A}', name: 'water wave', keywords: ['ocean', 'sea', 'summer'] },
      { char: '\u{1F30D}', name: 'globe showing Europe-Africa', keywords: ['world', 'earth', 'global'] },
    ],
  },
  {
    id: 'travel',
    label: 'Travel & Activity',
    emoji: [
      { char: '\u{2708}\u{FE0F}', name: 'airplane', keywords: ['travel', 'flight', 'trip'] },
      { char: '\u{1F3D6}\u{FE0F}', name: 'beach with umbrella', keywords: ['vacation', 'summer'] },
      { char: '\u{1F3DD}\u{FE0F}', name: 'desert island', keywords: ['vacation', 'getaway'] },
      { char: '\u{1F697}', name: 'automobile', keywords: ['car', 'drive'] },
      { char: '\u{1F3C1}', name: 'chequered flag', keywords: ['finish', 'race', 'launch'] },
      { char: '\u{1F680}', name: 'rocket', keywords: ['launch', 'fast', 'boost', 'growth'] },
      { char: '\u{1F3AF}', name: 'direct hit', keywords: ['target', 'goal', 'bullseye', 'aim'] },
      { char: '\u{1F3AE}', name: 'video game', keywords: ['play', 'game'] },
      { char: '\u{1F3B2}', name: 'game die', keywords: ['random', 'luck', 'chance'] },
      { char: '\u{1F3B5}', name: 'musical note', keywords: ['music', 'song'] },
      { char: '\u{1F3B8}', name: 'guitar', keywords: ['music'] },
      { char: '\u{1F3A8}', name: 'artist palette', keywords: ['art', 'design', 'creative', 'color'] },
      { char: '\u{1F4F8}', name: 'camera with flash', keywords: ['photo', 'picture'] },
      { char: '\u{1F3AC}', name: 'clapper board', keywords: ['film', 'video', 'movie'] },
      { char: '\u{26BD}', name: 'soccer ball', keywords: ['sport', 'football'] },
      { char: '\u{1F3C3}', name: 'person running', keywords: ['run', 'active', 'fast'] },
      { char: '\u{1F9D8}', name: 'person in lotus position', keywords: ['yoga', 'calm', 'wellness'] },
      { char: '\u{1F3AA}', name: 'circus tent', keywords: ['event', 'show', 'fun'] },
      { char: '\u{1F3B8}', name: 'guitar', keywords: ['music'] },
      { char: '\u{1F6CF}\u{FE0F}', name: 'bed', keywords: ['sleep', 'home', 'rest'] },
    ],
  },
  {
    id: 'symbols',
    label: 'Symbols',
    emoji: [
      { char: '\u{2705}', name: 'check mark button', keywords: ['done', 'yes', 'ok', 'success'] },
      { char: '\u{2714}\u{FE0F}', name: 'check mark', keywords: ['done', 'yes', 'tick'] },
      { char: '\u{274C}', name: 'cross mark', keywords: ['no', 'wrong', 'error', 'x'] },
      { char: '\u{2757}', name: 'exclamation mark', keywords: ['important', 'alert'] },
      { char: '\u{2753}', name: 'question mark', keywords: ['help', 'ask'] },
      { char: '\u{1F4A1}', name: 'light bulb', keywords: ['idea', 'tip', 'insight'] },
      { char: '\u{2B50}', name: 'star', keywords: ['favorite', 'rating'] },
      { char: '\u{1F51D}', name: 'top arrow', keywords: ['new', 'best'] },
      { char: '\u{1F195}', name: 'new button', keywords: ['new', 'fresh'] },
      { char: '\u{1F193}', name: 'free button', keywords: ['free', 'offer'] },
      { char: '\u{1F53A}', name: 'red triangle pointed up', keywords: ['up', 'increase'] },
      { char: '\u{1F4C8}', name: 'chart increasing', keywords: ['growth', 'up', 'results', 'stats'] },
      { char: '\u{1F4C9}', name: 'chart decreasing', keywords: ['down', 'drop'] },
      { char: '\u{1F4CA}', name: 'bar chart', keywords: ['stats', 'data', 'results'] },
      { char: '\u{2B06}\u{FE0F}', name: 'up arrow', keywords: ['up'] },
      { char: '\u{2B07}\u{FE0F}', name: 'down arrow', keywords: ['down'] },
      { char: '\u{27A1}\u{FE0F}', name: 'right arrow', keywords: ['next', 'go'] },
      { char: '\u{2B05}\u{FE0F}', name: 'left arrow', keywords: ['back'] },
      { char: '\u{1F449}', name: 'right pointer', keywords: ['this', 'here'] },
      { char: '\u{267B}\u{FE0F}', name: 'recycling symbol', keywords: ['eco', 'green', 'sustainable'] },
      { char: '\u{1F512}', name: 'locked', keywords: ['secure', 'private', 'safe'] },
      { char: '\u{1F513}', name: 'unlocked', keywords: ['open', 'access', 'unlock'] },
      { char: '\u{1F3AB}', name: 'ticket', keywords: ['event', 'admit', 'pass'] },
      { char: '\u{1F4CC}', name: 'pushpin', keywords: ['note', 'pin', 'important'] },
      { char: '\u{1F4DD}', name: 'memo', keywords: ['note', 'write', 'form'] },
    ],
  },
];

/** Flat list across all categories, in display order. */
export const ALL_EMOJI: EmojiEntry[] = EMOJI_CATEGORIES.flatMap((c) => c.emoji);

/**
 * Substring search over each emoji's name and keywords, case-insensitive.
 * Whitespace-only queries return the full list so an empty box shows everything.
 */
export function searchEmoji(query: string): EmojiEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return ALL_EMOJI;
  return ALL_EMOJI.filter(
    (e) => e.name.toLowerCase().includes(q) || (e.keywords ?? []).some((k) => k.includes(q)),
  );
}
